import type { ISelectOption } from './filterTypes';
import { isSetDate, toDate, MaybeDate } from '../../../Helpers/Dates';

/**
 * A UTC calendar day as 'YYYY-MM-DD'.
 *
 * Held as a string rather than a Date so it is serialisable, Redux-safe, and
 * comes straight out of <input type="date">.value. Zero-padded ISO sorts
 * lexicographically in chronological order, so range comparison needs no Date
 * arithmetic and cannot drift across DST or midnight boundaries.
 */
export type DayKey = string;

const pad2 = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

/**
 * UTC day key for a value, or null if the value is not a real date.
 *
 * UTC (not local) because the API serialises .NET DateTime as a UTC instant and
 * the existing date rendering (Dates.ts, DatePicker.tsx) already reads UTC
 * parts. Using local parts here would let a card display 1 March while the
 * filter matched it as 28 February for a user west of UTC.
 */
export const toDayKey = (v: MaybeDate): DayKey | null => {
  if (!isSetDate(v)) return null; // covers null/undefined/''/NaN/0001 sentinel/<=1901
  const d = toDate(v as Exclude<MaybeDate, null | undefined>);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
};

/** dd/mm/yyyy from a DayKey. String-only, so no Date and no timezone drift. */
export const formatDayKey = (key: DayKey): string => {
  const [y, m, d] = key.split('-');
  return `${d}/${m}/${y}`;
};

/** Inclusive bounds for a date-range filter. '' means that end is unset. */
export type DateRange = { from: DayKey | ''; to: DayKey | '' };

export const EMPTY_DATE_RANGE: DateRange = { from: '', to: '' };

/**
 * Read a date-range filter's bounds out of its degenerate option list.
 * The encoded form is `"<from>|<to>"`; DayKeys never contain '|'.
 */
export const dateRangeValue = (selected?: ISelectOption[]): DateRange => {
  if (!selected || selected.length === 0) return EMPTY_DATE_RANGE;
  const [from = '', to = ''] = selected[0].value.split('|');
  return { from, to };
};

/** Human-readable range for the dropdown button and the search-bar chip. */
export const formatDateRangeLabel = (from: DayKey | '', to: DayKey | ''): string => {
  if (from && to) return `${formatDayKey(from)} – ${formatDayKey(to)}`;
  if (from) return `From ${formatDayKey(from)}`;
  if (to) return `Until ${formatDayKey(to)}`;
  return '';
};

/**
 * Build (or clear) a date-range filter's value. Always replaces, never appends.
 * Returns [] when both ends are unset, so the filter reads as inactive and its
 * chip disappears.
 */
export const makeDateRangeOption = (
  from: string,
  to: string
): ISelectOption[] =>
  from || to
    ? [{ value: `${from}|${to}`, label: formatDateRangeLabel(from, to), required: false }]
    : [];

/**
 * Inclusive on both ends. An item with no usable completion date fails as soon
 * as either bound is set — "completed between X and Y" cannot be true of
 * something that was never completed. This matches the existing Completed For /
 * Completed By filters, which already exclude items with a blank value.
 */
export const inDateRange = (
  value: MaybeDate,
  from: DayKey | '',
  to: DayKey | ''
): boolean => {
  if (!from && !to) return true;
  const key = toDayKey(value);
  if (!key) return false;
  if (from && key < from) return false;
  if (to && key > to) return false;
  return true;
};

/**
 * One comparator for every data-driven option list.
 *
 * `sensitivity: 'base'` keeps 'alpha' and 'Alpha' adjacent; `numeric: true`
 * orders 'Site 2' before 'Site 10'.
 */
export const OPTION_COLLATOR = new Intl.Collator(undefined, {
  sensitivity: 'base',
  numeric: true,
});

/**
 * Sort options by label.
 *
 * Copies before sorting: useFilterOptions caches API responses in a module-level
 * map, so an in-place sort would permanently reorder the shared payload for
 * every later consumer.
 */
export const sortOptionsByLabel = (
  options: ISelectOption[] | undefined
): ISelectOption[] =>
  [...(options ?? [])].sort((a, b) => OPTION_COLLATOR.compare(a.label ?? '', b.label ?? ''));

/**
 * trim -> drop empty -> dedupe -> sort -> map to options.
 *
 * Dedupe is exact (case-sensitive), matching the Set-based dedupe this
 * replaced: 'Alan' and 'alan' are two distinct backend values, and collapsing
 * them would arbitrarily pick one casing. Only the ordering is
 * case-insensitive, so they sort adjacently.
 */
export const namesToSortedOptions = (names: Iterable<string>): ISelectOption[] =>
  Array.from(
    new Set(
      Array.from(names)
        .map((n) => (n ?? '').trim())
        .filter(Boolean)
    )
  )
    .sort(OPTION_COLLATOR.compare)
    .map((name) => ({ label: name, value: name, required: false }));
